'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  id: number;
  name: string;
  location: string;
  rating: number;
  text: string;
  image: string;
  imageAlt: string;
  service: string;
}

const TestimonialsSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Sarah Martinez',
    location: 'Scottsdale, AZ',
    rating: 5,
    text: 'Phoenix Patio Pro transformed our backyard! The pavers look brand new after years of desert wear. Their team was professional, punctual, and the results exceeded our expectations. Highly recommend for anyone in the Phoenix area!',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b23ad375-1763294352665.png",
    imageAlt: 'Professional headshot of Hispanic woman with long dark hair smiling warmly',
    service: 'Paver Restoration'
  },
  {
    id: 2,
    name: 'Michael Chen',
    location: 'Tempe, AZ',
    rating: 5,
    text: 'Outstanding service from start to finish. They removed stubborn monsoon stains from our concrete patio that we thought were permanent. The sealing they applied has kept it looking great through multiple dust storms. Worth every penny!',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1579b738c-1763295027219.png",
    imageAlt: 'Professional headshot of Asian man in blue shirt with friendly smile',
    service: 'Concrete Patio Cleaning'
  },
  {
    id: 3,
    name: 'Jennifer Thompson',
    location: 'Gilbert, AZ',
    rating: 5,
    text: 'We use Phoenix Patio Pro for our HOA community and they are fantastic. Always on time, thorough, and respectful of residents. The pool decks and common areas look immaculate. Great communication and competitive pricing for commercial work.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1456eb2f9-1763294356174.png",
    imageAlt: 'Professional headshot of Caucasian woman with blonde hair in business attire',
    service: 'Commercial Services'
  }];


  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 7000);

    return () => clearInterval(interval);
  }, [isHydrated, testimonials.length]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 md:py-24 bg-card">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading testimonials...</p>
          </div>
        </div>
      </section>);

  }

  return (
    <section className="py-16 md:py-24 bg-card">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-headline font-bold text-3xl md:text-4xl lg:text-5xl text-card-foreground mb-4">
              What Our Customers Say
            </h2>
            <p className="font-body text-lg md:text-xl text-muted-foreground">
              Real reviews from satisfied Phoenix Metro homeowners
            </p>
          </div>

          {/* Testimonial Display */}
          <div className="relative">
            <div className="bg-background rounded-lg shadow-brand-elevated p-8 md:p-12">
              {/* Quote Icon */}
              <div className="w-12 h-12 bg-desert-gradient rounded-full flex items-center justify-center mb-6 mx-auto">
                <Icon name="ChatBubbleLeftRightIcon" size={24} className="text-white" />
              </div>

              {/* Rating */}
              <div className="flex justify-center gap-1 mb-6">
                {[...Array(testimonials[currentIndex].rating)].map((_, i) =>
                <Icon key={i} name="StarIcon" size={24} className="text-warning" variant="solid" />
                )}
              </div>

              {/* Testimonial Text */}
              <blockquote className="font-body text-lg md:text-xl text-foreground text-center mb-8 leading-relaxed">
                "{testimonials[currentIndex].text}"
              </blockquote>

              {/* Customer Info */}
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full overflow-hidden mb-4 ring-4 ring-primary/20">
                  <AppImage
                    src={testimonials[currentIndex].image}
                    alt={testimonials[currentIndex].imageAlt}
                    className="w-full h-full object-cover" />

                </div>
                <div className="text-center">
                  <div className="font-headline font-semibold text-lg text-card-foreground">
                    {testimonials[currentIndex].name}
                  </div>
                  <div className="font-body text-muted-foreground">
                    {testimonials[currentIndex].location}
                  </div>
                  <div className="font-body text-sm text-primary mt-1">
                    {testimonials[currentIndex].service}
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={handlePrevious}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center hover:bg-primary/90 transition-brand shadow-brand"
              aria-label="Previous testimonial">

              <Icon name="ChevronLeftIcon" size={24} />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center hover:bg-primary/90 transition-brand shadow-brand"
              aria-label="Next testimonial">

              <Icon name="ChevronRightIcon" size={24} />
            </button>
          </div>

          {/* Indicators */}
          <div className="flex justify-center gap-3 mt-8">
            {testimonials.map((_, index) =>
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-3 h-3 rounded-full transition-brand ${
              index === currentIndex ? 'bg-primary w-8' : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'}`
              }
              aria-label={`Go to testimonial ${index + 1}`} />

            )}
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;